import Layout from "@/components/Layout";
import { Section, SectionLabel, SectionTitle } from "@/components/Section";

const LastUpdated = () => (
  <p className="text-sm text-muted-foreground font-body mb-8">
    Last updated: May 24, 2026
  </p>
);

const SectionHeading = ({ children }: { children: React.ReactNode }) => (
  <h2 className="font-display text-xl text-navy-deep mt-8 mb-3">{children}</h2>
);

const Body = ({ children }: { children: React.ReactNode }) => (
  <p className="text-text-light font-body leading-relaxed mb-4">{children}</p>
);

const List = ({ items }: { items: string[] }) => (
  <ul className="list-disc list-inside text-text-light font-body leading-relaxed mb-4 space-y-1">
    {items.map((item, i) => (
      <li key={i}>{item}</li>
    ))}
  </ul>
);

const Terms = () => (
  <Layout>
    <div className="bg-gradient-to-b from-white via-off-white to-white min-h-screen">
      <div className="max-w-[900px] mx-auto px-6 lg:px-10 pt-32 pb-20">
        <SectionLabel>Legal</SectionLabel>
        <SectionTitle>Terms &amp; Conditions</SectionTitle>
        <LastUpdated />

        <SectionHeading>1. Introduction</SectionHeading>
        <Body>
          Welcome to the website of Velocity Offshore Holdings LLC ("Velocity Offshore", "we", "us", or "our"). 
          These Terms and Conditions govern your access to and use of our website at www.velocityoffshore.com 
          (the "Site"), including any content, functionality, and services offered on or through the Site. 
          By accessing or using the Site, you accept and agree to be bound by these Terms and all applicable 
          laws and regulations. If you do not agree with any part of these Terms, you must not use the Site.
        </Body>

        <SectionHeading>2. Company Information</SectionHeading>
        <Body>
          Velocity Offshore Holdings LLC is incorporated in the Republic of the Marshall Islands. 
          Our operational headquarters are located in Houston, Texas, with global operations across 
          West Africa, the Caribbean, and South America. For inquiries, please contact us via the 
          details provided on our Contact page.
        </Body>

        <SectionHeading>3. Use of the Site</SectionHeading>
        <Body>
          You may use the Site solely for lawful purposes and in accordance with these Terms. 
          You agree not to use the Site:
        </Body>
        <List items={[
          "In any way that violates any applicable local, national, or international law or regulation.",
          "To transmit, or procure the sending of, any advertising or promotional material without our prior written consent.",
          "To impersonate or attempt to impersonate Velocity Offshore, an employee, another user, or any other person or entity.",
          "To engage in any other conduct that restricts or inhibits anyone's use or enjoyment of the Site, or which may harm Velocity Offshore or users of the Site.",
          "To introduce any viruses, Trojan horses, worms, logic bombs, or other material that is malicious or technologically harmful.",
        ]} />

        <SectionHeading>4. Intellectual Property Rights</SectionHeading>
        <Body>
          The Site and its entire contents, features, and functionality — including but not limited to 
          all information, software, text, displays, images, video, audio, and the design, selection, 
          and arrangement thereof — are owned by Velocity Offshore Holdings LLC, its licensors, or other 
          providers of such material and are protected by copyright, trademark, patent, trade secret, 
          and other intellectual property or proprietary rights laws.
        </Body>
        <Body>
          You may not reproduce, distribute, modify, create derivative works of, publicly display, 
          publicly perform, republish, download, store, or transmit any of the material on our Site 
          without our prior written consent, except as necessary for your own personal, non-commercial use.
        </Body>

        <SectionHeading>5. Client Portal and Employee Portal Access</SectionHeading>
        <Body>
          Access to the Client Portal and Employee Portal (collectively, the "Portals") is restricted 
          to authorised users only. Account credentials must be kept confidential. You are responsible 
          for all activities that occur under your account. Velocity Offshore reserves the right to 
          suspend or terminate access to the Portals at any time without notice for conduct that we 
          believe violates these Terms or is harmful to other users of the Portals, us, or third parties.
        </Body>

        <SectionHeading>6. Disclaimer of Warranties</SectionHeading>
        <Body>
          The Site is provided on an "as is" and "as available" basis without any warranties of any kind, 
          either express or implied. Velocity Offshore does not warrant that the Site will be uninterrupted, 
          timely, secure, or error-free, or that any defects will be corrected. Information on the Site 
          regarding vessel specifications, capabilities, and services is provided for general informational 
          purposes and does not constitute an offer or contractual representation.
        </Body>

        <SectionHeading>7. Limitation of Liability</SectionHeading>
        <Body>
          To the fullest extent permitted by law, Velocity Offshore Holdings LLC and its affiliates, 
          officers, directors, employees, agents, and licensors shall not be liable for any indirect, 
          incidental, special, consequential, or punitive damages, including but not limited to damages 
          for loss of profits, goodwill, use, data, or other intangible losses, resulting from your access 
          to or use of (or inability to access or use) the Site.
        </Body>

        <SectionHeading>8. Indemnification</SectionHeading>
        <Body>
          You agree to defend, indemnify, and hold harmless Velocity Offshore Holdings LLC and its 
          affiliates, licensors, and service providers, and its and their respective officers, directors, 
          employees, contractors, agents, licensors, suppliers, successors, and assigns from and against 
          any claims, liabilities, damages, judgments, awards, losses, costs, expenses, or fees 
          (including reasonable attorneys' fees) arising out of or relating to your violation of these 
          Terms or your use of the Site.
        </Body>

        <SectionHeading>9. Governing Law and Jurisdiction</SectionHeading>
        <Body>
          These Terms and any dispute or claim arising out of or in connection with them shall be 
          governed by and construed in accordance with the laws of the Republic of the Marshall Islands, 
          without regard to its conflict of law provisions. Any legal suit, action, or proceeding arising 
          out of or related to these Terms or the Site shall be instituted exclusively in the courts of 
          the Republic of the Marshall Islands.
        </Body>

        <SectionHeading>10. Changes to the Terms</SectionHeading>
        <Body>
          We may revise and update these Terms from time to time in our sole discretion. All changes 
          are effective immediately when we post them. Your continued use of the Site following the posting 
          of revised Terms means that you accept and agree to the changes.
        </Body>

        <SectionHeading>11. Contact Us</SectionHeading>
        <Body>
          If you have any questions about these Terms, please contact us through the Contact page on 
          this Site or write to Velocity Offshore Holdings LLC, Majuro, Marshall Islands.
        </Body>
      </div>
    </div>
  </Layout>
);

export default Terms;
